import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  BarChart3, Search, Loader2, ChevronLeft, ChevronRight, MapPin, Building2,
  BedDouble, Star, Landmark, Newspaper, Sparkles, Users, ShieldCheck, RefreshCw, ExternalLink, ListPlus, PlusCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';
const PROVIDER_TYPE_LABELS = {
  care_home: 'Care home', nursing_home: 'Nursing home',
  homecare: 'Homecare', supported_living: 'Supported living',
};

const RatingsBar = ({ rs }) => {
  const total = (rs?.outstanding || 0) + (rs?.good || 0) + (rs?.requires_improvement || 0) + (rs?.inadequate || 0);
  if (!total) return <span className="text-gray-400 text-xs">No ratings</span>;
  const seg = [
    ['outstanding', 'bg-emerald-500'], ['good', 'bg-sky-500'],
    ['requires_improvement', 'bg-amber-500'], ['inadequate', 'bg-rose-500'],
  ];
  return (
    <div className="flex h-2.5 w-32 rounded-full overflow-hidden bg-gray-100" title={`Outstanding ${rs.outstanding} · Good ${rs.good} · Requires imp. ${rs.requires_improvement} · Inadequate ${rs.inadequate}`}>
      {seg.map(([k, c]) => (rs[k] ? <div key={k} className={c} style={{ width: `${(rs[k] / total) * 100}%` }} /> : null))}
    </div>
  );
};

const MarketIntel = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.role === 'owner' || user?.role === 'admin';

  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [search, setSearch] = useState('');
  const [ptype, setPtype] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [minLoc, setMinLoc] = useState('2');

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [summarising, setSummarising] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [addingCrm, setAddingCrm] = useState(false);
  const [savingSegment, setSavingSegment] = useState(false);
  const [segDialog, setSegDialog] = useState(false);
  const [segName, setSegName] = useState('');

  const SEGMENTS = [
    { label: 'Care home groups', type: 'care_home' },
    { label: 'Nursing home groups', type: 'nursing_home' },
    { label: 'Homecare groups', type: 'homecare' },
    { label: 'Supported living groups', type: 'supported_living' },
    { label: 'Retirement (best-effort)', type: ALL, search: 'retirement' },
  ];

  const applySegment = (seg) => {
    setPage(1);
    setPtype(seg.type);
    setRegion(ALL);
    setSearch(seg.search || '');
    setMinLoc('2');
  };

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/market-intel/meta`);
      setMeta(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, min_locations: minLoc || 1 };
      if (search) params.search = search;
      if (ptype !== ALL) params.provider_type = ptype;
      if (region !== ALL) params.region = region;
      const { data } = await axios.get(`${API}/market-intel/groups`, { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load provider groups');
    } finally { setLoading(false); }
  }, [page, search, ptype, region, minLoc]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadGroups(); }, [loadGroups]);

  const runBuild = async () => {
    setBuilding(true);
    try {
      const { data } = await axios.post(`${API}/market-intel/build`);
      toast.success(`Built ${data.multi_site_groups} multi-site groups`);
      await loadMeta();
      await loadGroups();
    } catch (e) {
      toast.error('Build failed (admin only)');
    } finally { setBuilding(false); }
  };

  const openGroup = async (g) => {
    setSelected(g);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/market-intel/groups/${g.provider_id}`);
      setDetail(data);
    } catch (e) { toast.error('Failed to load group'); }
    finally { setDetailLoading(false); }
  };

  const enrich = async () => {
    setEnriching(true);
    try {
      const { data } = await axios.post(`${API}/market-intel/groups/${selected.provider_id}/enrich`);
      setDetail((d) => ({ ...d, ch: data, ch_enriched_at: new Date().toISOString() }));
      toast.success(data.matched ? `Matched ${data.company_name}` : 'No Companies House match');
      loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enrichment failed'); }
    finally { setEnriching(false); }
  };

  const loadNews = async () => {
    setNewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/market-intel/groups/${selected.provider_id}/news`);
      setDetail((d) => ({ ...d, news: data.items || [] }));
    } catch (e) { toast.error('News fetch failed'); }
    finally { setNewsLoading(false); }
  };

  const genSummary = async () => {
    setSummarising(true);
    try {
      const { data } = await axios.post(`${API}/market-intel/groups/${selected.provider_id}/ai-summary`);
      setDetail((d) => ({ ...d, ai_summary: data.ai_summary }));
      toast.success('AI summary generated');
      loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI summary failed'); }
    finally { setSummarising(false); }
  };

  const addToCrm = async () => {
    setAddingCrm(true);
    try {
      const { data } = await axios.post(`${API}/market-intel/groups/${selected.provider_id}/add-to-crm`);
      toast.success(data.created ? 'Added to CRM as a care provider' : 'Already in your CRM');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add to CRM failed'); }
    finally { setAddingCrm(false); }
  };

  const saveSegment = async () => {
    if (!segName.trim()) { toast.error('List name required'); return; }
    setSavingSegment(true);
    try {
      const body = { list_name: segName.trim(), min_locations: Number(minLoc) || 2, limit: 200 };
      if (search) body.search = search;
      if (ptype !== ALL) body.provider_type = ptype;
      if (region !== ALL) body.region = region;
      const { data } = await axios.post(`${API}/market-intel/add-segment-to-crm`, body);
      toast.success(`"${data.list_name}": ${data.list_members} groups (${data.companies_created} new) added to CRM`);
      setSegDialog(false);
      setSegName('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save segment'); }
    finally { setSavingSegment(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="market-intel-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#00aeef]" /> Market Intelligence
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Care provider groups from the CQC spine, enriched with Companies House ownership, loans, news &amp; AI summaries - all free data.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={runBuild} disabled={building} variant="outline" data-testid="mi-build-btn">
            {building ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {meta?.needs_build ? 'Build market intelligence' : 'Rebuild groups'}
          </Button>
        )}
      </div>

      {meta && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs text-gray-500">Multi-site groups</div><div className="text-2xl font-bold text-gray-900" data-testid="mi-stat-groups">{(meta.multi_site_groups || 0).toLocaleString()}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-gray-500">Providers indexed</div><div className="text-2xl font-bold text-gray-900">{(meta.built || 0).toLocaleString()}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-gray-500">Regions covered</div><div className="text-2xl font-bold text-gray-900">{(meta.regions || []).length}</div></CardContent></Card>
        </div>
      )}

      {meta?.needs_build ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          Market intelligence hasn't been built yet. {isAdmin ? 'Click "Build market intelligence" to roll up the CQC data into provider groups.' : 'Ask an admin to build it.'}
        </CardContent></Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="mi-segment-presets">
            <span className="text-xs text-gray-500 mr-1">Segments:</span>
            {SEGMENTS.map((seg) => (
              <button key={seg.label} type="button" onClick={() => applySegment(seg)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ptype === seg.type && (seg.search || '') === search ? 'bg-[#00aeef] text-white border-[#00aeef]' : 'border-gray-300 text-gray-600 hover:border-[#00aeef]'}`}
                data-testid={`mi-segment-${seg.type}`}>
                {seg.label}
              </button>
            ))}
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => setSegDialog(true)} data-testid="mi-save-segment-btn">
              <ListPlus className="w-4 h-4 mr-1" /> Save segment to CRM list
            </Button>
          </div>

          <Card><CardContent className="p-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search provider group by name…" value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }} data-testid="mi-search-input" />
            </div>
            <Select value={ptype} onValueChange={(v) => { setPage(1); setPtype(v); }}>
              <SelectTrigger className="w-44" data-testid="mi-type-filter"><SelectValue placeholder="Provider type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {(meta?.provider_types || []).map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={region} onValueChange={(v) => { setPage(1); setRegion(v); }}>
              <SelectTrigger className="w-44" data-testid="mi-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All regions</SelectItem>
                {(meta?.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={minLoc} onValueChange={(v) => { setPage(1); setMinLoc(v); }}>
              <SelectTrigger className="w-36" data-testid="mi-minloc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2+ locations</SelectItem>
                <SelectItem value="5">5+ locations</SelectItem>
                <SelectItem value="10">10+ locations</SelectItem>
                <SelectItem value="25">25+ locations</SelectItem>
                <SelectItem value="1">All (incl. single)</SelectItem>
              </SelectContent>
            </Select>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b">
                  <th className="p-3">Provider group</th>
                  <th className="p-3">Locations</th>
                  <th className="p-3">Beds</th>
                  <th className="p-3">Types</th>
                  <th className="p-3">CQC ratings</th>
                  <th className="p-3"></th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-gray-400">No groups match.</td></tr>
                  ) : rows.map((g) => (
                    <tr key={g.provider_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => openGroup(g)} data-testid={`mi-row-${g.provider_id}`}>
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{g.name}</div>
                        <div className="text-xs text-gray-400">{(g.regions || []).slice(0, 2).join(', ')}{g.regions?.length > 2 ? ` +${g.regions.length - 2}` : ''}</div>
                      </td>
                      <td className="p-3 font-semibold">{g.location_count}</td>
                      <td className="p-3">{g.total_beds || '-'}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(g.provider_types || []).slice(0, 2).map((t) => <Badge key={t} variant="outline" className="text-xs">{PROVIDER_TYPE_LABELS[t] || t}</Badge>)}
                        </div>
                      </td>
                      <td className="p-3"><RatingsBar rs={g.ratings_summary} /></td>
                      <td className="p-3 text-right">
                        {g.has_ai_summary && <Badge className="bg-violet-100 text-violet-700 text-xs mr-1">AI</Badge>}
                        {g.has_ch && <Badge className="bg-sky-100 text-sky-700 text-xs">CH</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">{total.toLocaleString()} groups</div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="mi-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="mi-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="mi-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#00aeef]" />{selected?.name}</DialogTitle>
            <DialogDescription>Care provider group - market intelligence record</DialogDescription>
            <div className="pt-1">
              <Button size="sm" variant="outline" onClick={addToCrm} disabled={addingCrm} data-testid="mi-add-to-crm-btn">
                {addingCrm ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-1" />} Add to CRM
              </Button>
            </div>
          </DialogHeader>

          {detailLoading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : detail && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><div className="text-xs text-gray-500">Locations</div><div className="text-lg font-bold flex items-center gap-1"><MapPin className="w-4 h-4 text-gray-400" />{detail.location_count}</div></div>
                <div><div className="text-xs text-gray-500">Registered beds</div><div className="text-lg font-bold flex items-center gap-1"><BedDouble className="w-4 h-4 text-gray-400" />{detail.total_beds}</div></div>
                <div><div className="text-xs text-gray-500">Councils</div><div className="text-lg font-bold">{(detail.councils || []).length}</div></div>
                <div><div className="text-xs text-gray-500">CQC ratings</div><RatingsBar rs={detail.ratings_summary} /></div>
              </div>

              {/* Companies House */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2"><Landmark className="w-4 h-4 text-[#00aeef]" />Company structure &amp; finance</h3>
                  {!detail.ch && <Button size="sm" onClick={enrich} disabled={enriching} data-testid="mi-enrich-btn">{enriching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}Enrich</Button>}
                </div>
                {!detail.ch ? (
                  <p className="text-sm text-gray-400">Not enriched yet. Click Enrich to pull directors, beneficial owners and charges/loans.</p>
                ) : detail.ch.matched === false ? (
                  <p className="text-sm text-gray-500">No Companies House match found for this provider name.</p>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                      <span><b>{detail.ch.company_name}</b> (#{detail.ch.company_number})</span>
                      <span className="text-gray-500">Status: {detail.ch.status}</span>
                      <span className="text-gray-500">Incorporated: {detail.ch.incorporated}</span>
                      {detail.ch.has_insolvency_history && <Badge className="bg-rose-100 text-rose-700 text-xs">Insolvency history</Badge>}
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" />Directors</div>
                      <div className="flex flex-wrap gap-1">
                        {(detail.ch.directors || []).length ? detail.ch.directors.map((d, i) => <Badge key={i} variant="outline" className="text-xs">{d.name}{d.role ? ` · ${d.role}` : ''}</Badge>) : <span className="text-gray-400 text-xs">None on file</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Beneficial owners (PSC)</div>
                      <div className="flex flex-wrap gap-1">
                        {(detail.ch.owners || []).length ? detail.ch.owners.map((o, i) => <Badge key={i} variant="outline" className="text-xs">{o.name}</Badge>) : <span className="text-gray-400 text-xs">None on file</span>}
                      </div>
                    </div>
                    <div className="text-gray-600">
                      Secured charges/loans: <b>{(detail.ch.charges || []).length}</b> on file ({detail.ch.open_charges || 0} outstanding) · Latest accounts to {detail.ch.accounts_last_made_up_to || 'n/a'}
                    </div>
                  </div>
                )}
              </div>

              {/* AI summary */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-500" />AI market summary</h3>
                  {user?.role === 'owner' && <Button size="sm" variant="outline" onClick={genSummary} disabled={summarising} data-testid="mi-ai-btn">{summarising ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}{detail.ai_summary ? 'Regenerate' : 'Generate'}</Button>}
                </div>
                {detail.ai_summary ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.ai_summary}</p> : <p className="text-sm text-gray-400">{user?.role === 'owner' ? 'Generate a concise market-intelligence brief from the CQC + Companies House signals.' : 'No AI summary yet. (Uses paid AI credits, so only the account owner can generate one.)'}</p>}
              </div>

              {/* News */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2"><Newspaper className="w-4 h-4 text-[#00aeef]" />Recent news</h3>
                  {user?.role === 'owner' && <Button size="sm" variant="outline" onClick={loadNews} disabled={newsLoading} data-testid="mi-news-btn">{newsLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Fetch</Button>}
                </div>
                {(detail.news || []).length ? (
                  <ul className="space-y-2">
                    {detail.news.map((n, i) => (
                      <li key={i} className="text-sm">
                        <a href={n.link} target="_blank" rel="noreferrer" className="text-[#0077b6] hover:underline flex items-start gap-1">
                          <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{n.title}
                        </a>
                        <div className="text-xs text-gray-400">{n.source} · {n.published}</div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-gray-400">Click Fetch to pull recent news mentions.</p>}
              </div>

              {/* Locations */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00aeef]" />Locations ({(detail.locations || []).length} shown)</h3>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-gray-400"><th className="p-1.5">Name</th><th className="p-1.5">Council</th><th className="p-1.5">Beds</th><th className="p-1.5">Rating</th></tr></thead>
                    <tbody>
                      {(detail.locations || []).map((l) => (
                        <tr key={l.locationId} className="border-t">
                          <td className="p-1.5">{l.name}</td>
                          <td className="p-1.5 text-gray-500">{l.localAuthority || '-'}</td>
                          <td className="p-1.5">{l.numberOfBeds || '-'}</td>
                          <td className="p-1.5">{l.overallRating || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={segDialog} onOpenChange={setSegDialog}>
        <DialogContent className="max-w-md" data-testid="mi-segment-dialog">
          <DialogHeader>
            <DialogTitle>Save segment as CRM list</DialogTitle>
            <DialogDescription>
              Adds the current filtered provider groups (up to 200, largest first) to your CRM as care-provider companies and a static list for outreach.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="List name e.g. Care home groups - North West" value={segName}
              onChange={(e) => setSegName(e.target.value)} data-testid="mi-segment-name-input" />
            <div className="text-xs text-gray-500">
              Filters: {ptype !== ALL ? PROVIDER_TYPE_LABELS[ptype] || ptype : 'All types'}
              {region !== ALL ? ` · ${region}` : ''}{search ? ` · "${search}"` : ''} · {minLoc}+ locations
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSegDialog(false)}>Cancel</Button>
              <Button onClick={saveSegment} disabled={savingSegment} className="bg-[#00aeef] hover:bg-[#0097cf]" data-testid="mi-segment-save-btn">
                {savingSegment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Add to CRM
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketIntel;
